import EDamageSeverityLevel from "@@constants/EDamageSeverityLevel";
import getDamageScore from "@@utils/getDamageScore";
import {
  Damage,
  GetDamageMatrixResponse,
  UpdateQualityControlCommand,
} from "@inbound/api";
import {
  ExteriorDamageLocationKeys,
  GenericDamageKeys,
  GenericDamageSmellKeys,
  HardwareDamageKeys,
  HardwareDamageLocationKeys,
  InteriorDamageLocationKeys,
  InteriorExteriorDamageOtherDamagesKeys,
  InteriorExteriorDamageStainedKeys,
  LocationKeys,
} from "types/damages";
import pascalize from "utils/pascalize";
import * as yup from "yup";

export enum EDamage {
  // Interior/Exterior Stained
  Mold = 30,
  OtherStain = 7,
  PenMark = 6,
  TransferOfColor = 5,
  WaterMark = 4,

  // Interior/Exterior Smells
  Cigarette = 10,
  Musty = 9,
  OtherSmell = 11,
  Perfume = 8,

  // Interior/Exterior Other Damages
  Cracked = 15,
  Creased = 27,
  CutStitching = 25,
  Discolored = 12,
  LooseStitching = 16,
  OutOfShape = 19,
  Peeling = 14,
  Personalized = 29,
  Powdery = 32,
  Repaired = 28,
  Ripped = 13,
  Split = 26,
  Sticky = 17,
  Worn = 18,

  // Hardware
  Damaged = 21,
  Missing = 24,
  RustyOrTarnished = 23,

  // Hardware/Interior/Exterior
  Scratched = 20,
  Repainted = 31,
}

export enum EDamageLocation {
  // General
  Smell = 24,

  // Exterior
  Back = 5,
  Bottom = 6,
  Corners = 9,
  Front = 4,
  HandleOrStrap = 7,
  LeatherOrFabricZipperPull = 29,
  Pocket = 8,
  Side = 10,
  Top = 21,

  // Interior
  InteriorLeatherOrFabricZipperPull = 30,
  InteriorPocket = 12,
  Lining = 11,

  // Hardware
  Closure = 18,
  Embellishment = 22,
  Key = 16,
  PracticalAttachment = 23,
  Zipper = 13,
}
export interface IDamageSeverity {
  id: EDamage;
  severityId: EDamageSeverityLevel;
}

export interface IDamage extends Damage {
  score?: number;
}

export interface IEditDamage {
  id?: number | null;
  score?: number | null;
  severityId?: number | null;
}
export interface IDamages {
  locationId?: number;
  damages?: IEditDamage[];
}

export interface IDamageData extends IDamages {
  countByLocation: number;
  locationName: string;
  parentLocation: string;
}

export const damageSchema = yup.object().shape({
  id: yup.number().required(),
  score: yup.number().optional(),
  severityId: yup.number().required(),
});

export interface IInteriorExteriorDamages {
  otherDamages: {
    [key in InteriorExteriorDamageOtherDamagesKeys]: IEditDamage;
  };
  stained: {
    [key in InteriorExteriorDamageStainedKeys]: IEditDamage;
  };
}

export type TQualityCheckExteriorDamageInputs = {
  [key in ExteriorDamageLocationKeys]: IInteriorExteriorDamages;
};

export type TQualityCheckInteriorDamageInputs = {
  [key in InteriorDamageLocationKeys]: IInteriorExteriorDamages;
};

export type THardwareDamages = {
  [key in HardwareDamageKeys]: IEditDamage;
};

export type TQualityCheckHardwareDamageInputs = {
  [key in HardwareDamageLocationKeys]: THardwareDamages;
};

export type TGenericDamages = {
  [key in GenericDamageSmellKeys]: IEditDamage;
};

export type TQualityCheckGenericDamageInputs = {
  [key in GenericDamageKeys]: TGenericDamages;
};

export type IEditDamageSchema = TQualityCheckExteriorDamageInputs &
  TQualityCheckInteriorDamageInputs &
  TQualityCheckHardwareDamageInputs &
  TQualityCheckGenericDamageInputs;

const interiorExteriorDamagesSchema = yup
  .object()
  .shape({
    otherDamages: yup
      .object()
      .shape({
        cracked: damageSchema,
        creased: damageSchema,
        cutStitching: damageSchema,
        discolored: damageSchema,
        looseStitching: damageSchema,
        outOfShape: damageSchema,
        peeling: damageSchema,
        personalized: damageSchema,
        powdery: damageSchema,
        repaired: damageSchema,
        repainted: damageSchema,
        ripped: damageSchema,
        scratched: damageSchema,
        split: damageSchema,
        sticky: damageSchema,
        worn: damageSchema,
      })
      .notRequired(),
    stained: yup
      .object()
      .shape({
        mold: damageSchema,
        others: damageSchema,
        penMark: damageSchema,
        transferOfColor: damageSchema,
        waterMark: damageSchema,
      })
      .notRequired(),
  })
  .notRequired();

const hardwareDamagesSchema = yup
  .object()
  .shape({
    damaged: damageSchema,
    missing: damageSchema,
    repaired: damageSchema,
    rusty: damageSchema,
    scratched: damageSchema,
    tarnished: damageSchema,
  })
  .notRequired();

const genericDamagesSchema = yup
  .object()
  .shape({
    cigarette: damageSchema,
    musty: damageSchema,
    others: damageSchema,
    perfume: damageSchema,
  })
  .notRequired();

export const editDamageSchema = yup.object().shape({
  back: interiorExteriorDamagesSchema,
  bottom: interiorExteriorDamagesSchema,
  buckle: hardwareDamagesSchema,
  corners: interiorExteriorDamagesSchema,
  front: interiorExteriorDamagesSchema,
  handle: interiorExteriorDamagesSchema,
  interiorLeatherOrFabricZipperPull: interiorExteriorDamagesSchema,
  interiorPocket: interiorExteriorDamagesSchema,
  key: hardwareDamagesSchema,
  leatherOrFabricZipperPull: interiorExteriorDamagesSchema,
  lining: interiorExteriorDamagesSchema,
  lock: hardwareDamagesSchema,
  metalAttachment: hardwareDamagesSchema,
  padlock: hardwareDamagesSchema,
  pocket: interiorExteriorDamagesSchema,
  screw: hardwareDamagesSchema,
  side: interiorExteriorDamagesSchema,
  smell: genericDamagesSchema,
  stud: hardwareDamagesSchema,
  zipper: hardwareDamagesSchema,
});

export const defaultDamage = {
  id: undefined,
  severityId: null,
};

const filterDamagePerLocation = (
  damages: IDamage[],
  locationId: EDamageLocation,
  damageId: EDamage,
  matrix: GetDamageMatrixResponse[],
): IEditDamage => {
  const damage = damages.find(
    ({ locationId: dLocationId, id }) =>
      locationId === dLocationId && id === damageId,
  );
  return damage
    ? {
        id: damage.id,
        severityId: damage.severity,
        score: getDamageScore(matrix, {
          id: damage.id,
          locationId,
          severity: damage.severity,
        }),
      }
    : defaultDamage;
};

export const getEditDamages = (
  damages: IDamage[],
  matrix: GetDamageMatrixResponse[],
): IEditDamageSchema => {
  const initializeDamages = (key: LocationKeys, location: EDamageLocation) => {
    switch (key) {
      case "exterior":
      case "interior":
        return {
          otherDamages: {
            cracked: filterDamagePerLocation(
              damages,
              location,
              EDamage.Cracked,
              matrix,
            ),
            creased: filterDamagePerLocation(
              damages,
              location,
              EDamage.Creased,
              matrix,
            ),
            cutStitching: filterDamagePerLocation(
              damages,
              location,
              EDamage.CutStitching,
              matrix,
            ),
            discolored: filterDamagePerLocation(
              damages,
              location,
              EDamage.Discolored,
              matrix,
            ),
            looseStitching: filterDamagePerLocation(
              damages,
              location,
              EDamage.LooseStitching,
              matrix,
            ),
            outOfShape: filterDamagePerLocation(
              damages,
              location,
              EDamage.OutOfShape,
              matrix,
            ),
            peeling: filterDamagePerLocation(
              damages,
              location,
              EDamage.Peeling,
              matrix,
            ),
            personalized: filterDamagePerLocation(
              damages,
              location,
              EDamage.Personalized,
              matrix,
            ),
            powdery: filterDamagePerLocation(
              damages,
              location,
              EDamage.Powdery,
              matrix,
            ),
            repainted: filterDamagePerLocation(
              damages,
              location,
              EDamage.Repainted,
              matrix,
            ),
            repaired: filterDamagePerLocation(
              damages,
              location,
              EDamage.Repaired,
              matrix,
            ),
            ripped: filterDamagePerLocation(
              damages,
              location,
              EDamage.Ripped,
              matrix,
            ),
            scratched: filterDamagePerLocation(
              damages,
              location,
              EDamage.Scratched,
              matrix,
            ),
            split: filterDamagePerLocation(
              damages,
              location,
              EDamage.Split,
              matrix,
            ),
            sticky: filterDamagePerLocation(
              damages,
              location,
              EDamage.Sticky,
              matrix,
            ),
            worn: filterDamagePerLocation(
              damages,
              location,
              EDamage.Worn,
              matrix,
            ),
          },
          stained: {
            mold: filterDamagePerLocation(
              damages,
              location,
              EDamage.Mold,
              matrix,
            ),
            others: filterDamagePerLocation(
              damages,
              location,
              EDamage.OtherStain,
              matrix,
            ),
            penMark: filterDamagePerLocation(
              damages,
              location,
              EDamage.PenMark,
              matrix,
            ),
            transferOfColor: filterDamagePerLocation(
              damages,
              location,
              EDamage.TransferOfColor,
              matrix,
            ),
            waterMark: filterDamagePerLocation(
              damages,
              location,
              EDamage.WaterMark,
              matrix,
            ),
          },
        };
      case "generic":
        return {
          cigarette: filterDamagePerLocation(
            damages,
            location,
            EDamage.Cigarette,
            matrix,
          ),
          musty: filterDamagePerLocation(
            damages,
            location,
            EDamage.Musty,
            matrix,
          ),
          others: filterDamagePerLocation(
            damages,
            location,
            EDamage.OtherSmell,
            matrix,
          ),
          perfume: filterDamagePerLocation(
            damages,
            location,
            EDamage.Perfume,
            matrix,
          ),
        };
      case "hardware":
        return {
          damaged: filterDamagePerLocation(
            damages,
            location,
            EDamage.Damaged,
            matrix,
          ),
          missing: filterDamagePerLocation(
            damages,
            location,
            EDamage.Missing,
            matrix,
          ),
          repaired: filterDamagePerLocation(
            damages,
            location,
            EDamage.Repaired,
            matrix,
          ),
          rustyOrTarnished: filterDamagePerLocation(
            damages,
            location,
            EDamage.RustyOrTarnished,
            matrix,
          ),
          scratched: filterDamagePerLocation(
            damages,
            location,
            EDamage.Scratched,
            matrix,
          ),
        };
    }
  };

  return {
    back: initializeDamages(
      "exterior",
      EDamageLocation.Back,
    ) as IInteriorExteriorDamages,
    bottom: initializeDamages(
      "exterior",
      EDamageLocation.Bottom,
    ) as IInteriorExteriorDamages,
    closure: initializeDamages(
      "hardware",
      EDamageLocation.Closure,
    ) as THardwareDamages,
    corners: initializeDamages(
      "exterior",
      EDamageLocation.Corners,
    ) as IInteriorExteriorDamages,
    embellishment: initializeDamages(
      "hardware",
      EDamageLocation.Embellishment,
    ) as THardwareDamages,
    front: initializeDamages(
      "exterior",
      EDamageLocation.Front,
    ) as IInteriorExteriorDamages,
    handleOrStrap: initializeDamages(
      "exterior",
      EDamageLocation.HandleOrStrap,
    ) as IInteriorExteriorDamages,
    interiorLeatherOrFabricZipperPull: initializeDamages(
      "interior",
      EDamageLocation.InteriorLeatherOrFabricZipperPull,
    ) as IInteriorExteriorDamages,
    interiorPocket: initializeDamages(
      "exterior",
      EDamageLocation.InteriorPocket,
    ) as IInteriorExteriorDamages,
    key: initializeDamages("hardware", EDamageLocation.Key) as THardwareDamages,
    leatherOrFabricZipperPull: initializeDamages(
      "exterior",
      EDamageLocation.LeatherOrFabricZipperPull,
    ) as IInteriorExteriorDamages,
    lining: initializeDamages(
      "interior",
      EDamageLocation.Lining,
    ) as IInteriorExteriorDamages,
    pocket: initializeDamages(
      "exterior",
      EDamageLocation.Pocket,
    ) as IInteriorExteriorDamages,
    practicalAttachment: initializeDamages(
      "hardware",
      EDamageLocation.PracticalAttachment,
    ) as THardwareDamages,
    side: initializeDamages(
      "exterior",
      EDamageLocation.Side,
    ) as IInteriorExteriorDamages,
    smell: initializeDamages(
      "generic",
      EDamageLocation.Smell,
    ) as TGenericDamages,
    top: initializeDamages(
      "exterior",
      EDamageLocation.Top,
    ) as IInteriorExteriorDamages,
    zipper: initializeDamages(
      "hardware",
      EDamageLocation.Zipper,
    ) as THardwareDamages,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatDamages = (damage: any): any =>
  Object.values(damage)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .flatMap((d: any) => {
      if (Object.hasOwn(d, "severityId")) {
        return { severityId: d?.severityId, id: d?.id, score: d?.score };
      }
      return formatDamages(d);
    })
    .filter((damage) => damage?.severityId !== null);

export const formatEditDamageValues = (
  damages: IEditDamageSchema,
): UpdateQualityControlCommand => {
  return {
    itemDamages: Object.entries(damages)
      .map(([key, damages]) => ({
        locationId: Number(
          EDamageLocation[pascalize(key) as unknown as EDamageLocation],
        ),
        damages: formatDamages(damages),
      }))
      .filter((damage) => !!damage.damages.length),
  };
};
